import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'

import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellText,
	CellTextDate,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'
import { DataTableColumnHeader } from '@/components/ui/table/data-table/data-table-column-header'

import { outletMasterList } from '@/features/outlet/api'
import type { IOutlet } from '@/features/outlet/type'

export const Route = createFileRoute('/_app/settings/outlet/')({
	component: Component,
})

function Component() {
	return (
		<PageLayout.Root>
			<PageLayout.Header
				breadcrumbs={[
					['/settings', 'Pengaturan'],
					['/settings/outlet', 'Outlet'],
				]}
			/>
			<PageLayout.ContentSingleColumn>
				<PageCard.Root>
					<PageCard.Header
						title="Daftar Outlet"
						actions={
							<PageCard.HeaderActionRoot>
								<PageCard.HeaderAction asChild>
									<Link from={Route.fullPath} to="/settings/outlet/create">
										Tambah
									</Link>
								</PageCard.HeaderAction>
							</PageCard.HeaderActionRoot>
						}
					/>
					<OutletTable />
				</PageCard.Root>
			</PageLayout.ContentSingleColumn>
		</PageLayout.Root>
	)
}

function OutletTable() {
	const { data, isLoading } = useQuery(outletMasterList.qry())
	const columns = useColumns()
	const { table } = useDataTable({
		columns,
		data: data?.outlets ?? [],
	})

	return <DataTable table={table} columns={columns} isLoading={isLoading} />
}

const columnHelper = createColumnHelper<IOutlet>()
const useColumns = () => {
	const columns = useMemo(() => {
		return [
			columnHelper.accessor('name', {
				header: ({ column }) => (
					<DataTableColumnHeader title="Outlet" column={column} />
				),
				cell: ({ row }) => (
					<Link
						from={Route.fullPath}
						to="/settings/outlet/$id"
						params={{ id: row.original.id }}
					>
						<CellText text={row.original.name} />
					</Link>
				),
			}),
			columnHelper.accessor('email', {
				header: () => 'Email',
				cell: ({ row }) => <CellText text={row.original.email} />,
			}),
			columnHelper.accessor('phone', {
				header: () => 'No. Telepon',
				cell: ({ row }) => <CellText text={row.original.phone} />,
			}),
			columnHelper.accessor('address', {
				header: () => 'Alamat',
				cell: ({ row }) => <CellText text={row.original.address} />,
			}),
			columnHelper.accessor('createdAt', {
				header: () => 'Created At',
				cell: ({ row }) => <CellTextDate date={row.original.createdAt} />,
			}),
			columnHelper.accessor('updatedAt', {
				header: () => 'Updated At',
				cell: ({ row }) => <CellTextDate date={row.original.updatedAt} />,
			}),
		]
	}, [])

	return columns
}
